import { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "antd";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

import { HandbookMainFields } from "./HandbookMainFields";
import { buildHandbookPayload } from "./handbookFormPayload";
import {
  HandbookFormProps,
  toPayloadOptions,
  toRefFieldKeys,
} from "./handbookFormProps";
import { buildInitialValues } from "./handbookFormValues";
import { HandbookRefFields } from "./HandbookRefFields";
import { TerritorialUnitFields } from "./TerritorialUnitFields";

export default function HandbookFormModal(props: HandbookFormProps) {
  const { open, loading, initial, onCancel, onSubmit, isTerritorialUnit } =
    props;

  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [selectedRegion, setSelectedRegion] = useState<any>(null);

  const language = i18n.language === "ru" ? "ru" : "ky";

  const refFieldKeys = useMemo(
    () => toRefFieldKeys(props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.categoryFieldKey,
      props.typeFieldKey,
      props.cropFieldKey,
      props.bankFieldKey,
      props.unitMeasurementFieldKey,
    ],
  );

  useEffect(() => {
    if (!open) return;
    if (!form) return;

    form.resetFields();
    if (!initial) return;

    setSelectedRegion(initial.region ?? null);
    form.setFieldsValue(buildInitialValues(initial, refFieldKeys));
  }, [open, initial, form, refFieldKeys]);

  const handleOk = async () => {
    const values = await form.validateFields().catch((err) => {
      const first = err?.errorFields?.[0];
      if (first?.name) form.scrollToField(first.name);
      return null;
    });
    if (!values) return;

    onSubmit(buildHandbookPayload(values, toPayloadOptions(props, refFieldKeys)));
  };

  const isEdit = !!initial?.nameRu;

  return (
    <Modal
      open={open}
      title={isEdit ? t("handbook.table.edit") : t("handbook.table.create")}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText={isEdit ? t("handbook.table.save") : t("handbook.table.create")}
      cancelText={t("handbook.table.cancel")}
      centered
      destroyOnHidden
      width={{ xs: "100%", sm: 520, md: 560 }}
    >
      <Form form={form} layout="vertical">
        <HandbookMainFields
          {...props}
          form={form}
          language={language}
          selectedRegion={selectedRegion}
          onRegionChange={setSelectedRegion}
        />

        <HandbookRefFields {...props} language={language} />

        {isTerritorialUnit && <TerritorialUnitFields form={form} />}
      </Form>
    </Modal>
  );
}
